import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Tag, Progress, Alert, Typography, Row, Col, Input, Select } from 'antd';
import { ReloadOutlined, ClearOutlined, SearchOutlined } from '@ant-design/icons';

import { avaliacoesAdminService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import DetalheTurmaAvaliacaoModal from './DetalheTurmaAvaliacaoModal';

const { Text } = Typography;

// Situação (Parte 2 do ajuste de usabilidade admin) -- DERIVADA das
// contagens já existentes no payload (total_pendente/total_rascunho/
// total_aguardando_validacao/total_publicada), nunca um status
// persistido novo. total==0 é tratado como "Não iniciada" (não há nada a
// concluir). Fase 12 (validação administrativa): 4 estágios, na mesma
// ordem de progresso do fluxo real (PENDENTE -> RASCUNHO ->
// AGUARDANDO_VALIDACAO -> PUBLICADA).
const SITUACAO_NAO_INICIADA = 'NAO_INICIADA';
const SITUACAO_EM_ANDAMENTO = 'EM_ANDAMENTO';
const SITUACAO_AGUARDANDO_VALIDACAO = 'AGUARDANDO_VALIDACAO';
const SITUACAO_PUBLICADA = 'PUBLICADA';

const derivarSituacao = (registro) => {
  const total = registro.total_elegiveis_atual || 0;
  const publicada = registro.total_publicada || 0;
  const aguardando = registro.total_aguardando_validacao || 0;
  const rascunho = registro.total_rascunho || 0;
  if (total === 0) return SITUACAO_NAO_INICIADA;
  if (publicada === total) return SITUACAO_PUBLICADA;
  if (rascunho === 0 && publicada === 0 && aguardando > 0) return SITUACAO_AGUARDANDO_VALIDACAO;
  if (rascunho === 0 && aguardando === 0 && publicada === 0) return SITUACAO_NAO_INICIADA;
  return SITUACAO_EM_ANDAMENTO;
};

const FILTROS_INICIAIS = { turma: '', equipe: null, situacao: null, inconsistencias: null };

// Contrato real: GET /api/avaliacoes/periodos/{id}/acompanhamento
// (AcompanhamentoTurmaResponse) -- contagens já agregadas pelo backend,
// nunca recalculadas aqui (inclusive inconsistências, recalculadas a
// cada acesso no próprio backend, ver avaliacao_acompanhamento_service.py).
// Sem polling automático nesta V1 -- só o botão "Atualizar" explícito.
//
// Filtros/ordenação/resumo (ajuste de usabilidade admin) são inteiramente
// client-side: o endpoint já devolve o array completo de turmas do
// período (dezenas, não milhares), sem paginação backend -- nenhum
// endpoint novo, nenhuma alteração de contrato.
const PeriodoAcompanhamentoPainel = ({ periodo }) => {
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [turmaSelecionada, setTurmaSelecionada] = useState(null); // id_turma | null
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesAdminService.obterAcompanhamento(periodo.id);
      setLinhas(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Período ou turma não encontrado.');
      setErro(mensagem);
      setLinhas([]);
    } finally {
      setLoading(false);
    }
  }, [periodo.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const filtroAtivo =
    filtros.turma.trim() !== '' || filtros.equipe != null || filtros.situacao != null || filtros.inconsistencias != null;

  const limparFiltros = () => setFiltros(FILTROS_INICIAIS);

  // Opções do Select de Professor: derivadas do próprio payload já
  // carregado (sem duplicatas), nunca uma nova chamada ao backend.
  const opcoesProfessor = useMemo(
    () => Array.from(new Set(linhas.map((l) => l.nome_equipe).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [linhas],
  );

  // Filtragem: sempre produz um array NOVO (nunca muta `linhas`).
  const linhasFiltradas = useMemo(() => {
    const termoTurma = filtros.turma.trim().toLowerCase();
    return linhas.filter((registro) => {
      if (termoTurma && !String(registro.nome_turma || '').toLowerCase().includes(termoTurma)) return false;
      if (filtros.equipe && registro.nome_equipe !== filtros.equipe) return false;
      if (filtros.situacao && derivarSituacao(registro) !== filtros.situacao) return false;
      if (filtros.inconsistencias === 'COM' && !(registro.total_inconsistencias_atual > 0)) return false;
      if (filtros.inconsistencias === 'SEM' && registro.total_inconsistencias_atual > 0) return false;
      return true;
    });
  }, [linhas, filtros]);

  // Resumo administrativo (Parte 4) -- calculado exclusivamente a partir
  // do conjunto FILTRADO, com os dados já carregados (nenhum endpoint novo).
  const resumo = useMemo(() => {
    const turmasCount = linhasFiltradas.length;
    const alunos = linhasFiltradas.reduce((soma, r) => soma + (r.total_elegiveis_atual || 0), 0);
    const publicadas = linhasFiltradas.reduce((soma, r) => soma + (r.total_publicada || 0), 0);
    const inconsistencias = linhasFiltradas.reduce((soma, r) => soma + (r.total_inconsistencias_atual || 0), 0);
    return { turmasCount, alunos, publicadas, inconsistencias };
  }, [linhasFiltradas]);

  const columns = [
    {
      title: 'Turma',
      dataIndex: 'nome_turma',
      key: 'nome_turma',
      sorter: (a, b) => String(a?.nome_turma || '').localeCompare(String(b?.nome_turma || '')),
    },
    {
      title: 'Professor',
      dataIndex: 'nome_equipe',
      key: 'nome_equipe',
      sorter: (a, b) => String(a?.nome_equipe || '').localeCompare(String(b?.nome_equipe || '')),
    },
    {
      title: 'Total',
      dataIndex: 'total_elegiveis_atual',
      key: 'total_elegiveis_atual',
      width: 80,
      sorter: (a, b) => (Number(a?.total_elegiveis_atual) || 0) - (Number(b?.total_elegiveis_atual) || 0),
    },
    {
      title: 'Não iniciadas',
      dataIndex: 'total_pendente',
      key: 'total_pendente',
      width: 120,
      sorter: (a, b) => (Number(a?.total_pendente) || 0) - (Number(b?.total_pendente) || 0),
    },
    {
      title: 'Em andamento',
      dataIndex: 'total_rascunho',
      key: 'total_rascunho',
      width: 120,
      sorter: (a, b) => (Number(a?.total_rascunho) || 0) - (Number(b?.total_rascunho) || 0),
    },
    {
      title: 'Aguardando validação',
      dataIndex: 'total_aguardando_validacao',
      key: 'total_aguardando_validacao',
      width: 150,
      sorter: (a, b) => (Number(a?.total_aguardando_validacao) || 0) - (Number(b?.total_aguardando_validacao) || 0),
    },
    {
      title: 'Publicadas',
      key: 'progresso',
      width: 200,
      sorter: (a, b) => (Number(a?.total_publicada) || 0) - (Number(b?.total_publicada) || 0),
      render: (_, registro) => {
        const total = registro.total_elegiveis_atual || 0;
        const percentual = total > 0 ? Math.round((registro.total_publicada / total) * 100) : 0;
        return (
          <div style={{ minWidth: 160 }}>
            <Text style={{ fontSize: 12 }}>
              {registro.total_publicada} / {total} publicadas
            </Text>
            <Progress percent={percentual} size="small" />
          </div>
        );
      },
    },
    {
      title: 'Inconsistências',
      dataIndex: 'total_inconsistencias_atual',
      key: 'total_inconsistencias_atual',
      width: 130,
      sorter: (a, b) => (Number(a?.total_inconsistencias_atual) || 0) - (Number(b?.total_inconsistencias_atual) || 0),
      render: (valor) =>
        valor > 0 ? (
          <Tag color="warning">{valor} inconsistência{valor > 1 ? 's' : ''}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Buscar turma"
            prefix={<SearchOutlined />}
            value={filtros.turma}
            onChange={(e) => handleFiltroChange('turma', e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Todos os professores"
            value={filtros.equipe}
            onChange={(valor) => handleFiltroChange('equipe', valor ?? null)}
            allowClear
            showSearch
            style={{ width: '100%' }}
            options={opcoesProfessor.map((nome) => ({ value: nome, label: nome }))}
          />
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            placeholder="Todas as situações"
            value={filtros.situacao}
            onChange={(valor) => handleFiltroChange('situacao', valor ?? null)}
            allowClear
            style={{ width: '100%' }}
            options={[
              { value: SITUACAO_NAO_INICIADA, label: 'Não iniciada' },
              { value: SITUACAO_EM_ANDAMENTO, label: 'Em andamento' },
              { value: SITUACAO_AGUARDANDO_VALIDACAO, label: 'Aguardando validação' },
              { value: SITUACAO_PUBLICADA, label: 'Publicada' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            placeholder="Todas as inconsistências"
            value={filtros.inconsistencias}
            onChange={(valor) => handleFiltroChange('inconsistencias', valor ?? null)}
            allowClear
            style={{ width: '100%' }}
            options={[
              { value: 'COM', label: 'Com inconsistências' },
              { value: 'SEM', label: 'Sem inconsistências' },
            ]}
          />
        </Col>
        <Col xs={24} md={2}>
          <Button icon={<ClearOutlined />} onClick={limparFiltros} disabled={!filtroAtivo} block>
            Limpar
          </Button>
        </Col>
      </Row>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Text type="secondary">
          {resumo.turmasCount} turma{resumo.turmasCount === 1 ? '' : 's'} · {resumo.alunos} aluno{resumo.alunos === 1 ? '' : 's'} ·{' '}
          {resumo.publicadas} publicada{resumo.publicadas === 1 ? '' : 's'} · {resumo.inconsistencias} inconsistência{resumo.inconsistencias === 1 ? '' : 's'}
        </Text>
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>
          Atualizar
        </Button>
      </div>

      {erro && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar"
          description={erro}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          }
        />
      )}

      <Table
        rowKey="id_avaliacao_turma"
        columns={columns}
        dataSource={erro ? [] : linhasFiltradas}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{
          emptyText: erro
            ? ' '
            : linhas.length > 0
              ? 'Nenhuma turma corresponde aos filtros.'
              : 'Nenhuma turma incluída neste período ainda.',
        }}
        scroll={{ x: 1000 }}
        onRow={(registro) => ({
          onClick: () => setTurmaSelecionada(registro.id_turma),
          style: { cursor: 'pointer' },
        })}
      />

      <DetalheTurmaAvaliacaoModal
        open={turmaSelecionada != null}
        periodoId={periodo.id}
        turmaId={turmaSelecionada}
        onClose={() => setTurmaSelecionada(null)}
      />
    </div>
  );
};

export default PeriodoAcompanhamentoPainel;
